<template>
  <!-- Global search -->
  <button
    v-if="globalSearch.canUse()"
    @click="globalSearch.open()"
    data-tour="global-search-trigger"
    class="p-2 rounded-lg bg-icon-btn dark:bg-icon-btn-dark transition-colors touch-target flex items-center justify-center"
    title="Search (/)"
  >
    <MagnifyingGlassIcon class="h-5 w-5 text-fg-secondary dark:text-fg-muted-dark" />
  </button>

  <!-- Theme toggle -->
  <button
    @click="$emit('toggle-theme')"
    data-tour="theme-toggle"
    class="p-2 rounded-lg bg-icon-btn dark:bg-icon-btn-dark transition-colors touch-target flex items-center justify-center"
    :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
  >
    <MoonSolidIcon v-if="isDarkMode" class="h-5 w-5 text-blue-400" />
    <MoonOutlineIcon v-else class="h-5 w-5 text-fg-secondary dark:text-fg-secondary-dark" />
  </button>

  <!-- Actions menu — task-oriented actions (signups, teacher actions, etc), kept
       separate from the user menu below which is account/profile-scoped. Only
       rendered when a consumer actually supplies items, so views with nothing
       to put here (e.g. the admin panel) don't show an empty button. -->
  <div v-if="$slots['action-items']" class="relative" ref="actionsMenuRef" data-tour="actions-menu-wrapper">
    <button
      @click.stop="actionsOpen = !actionsOpen"
      data-tour="actions-menu-trigger"
      class="p-2 rounded-lg bg-icon-btn dark:bg-icon-btn-dark transition-colors touch-target flex items-center justify-center"
      title="Actions"
      :aria-expanded="actionsOpen"
      aria-haspopup="true"
    >
      <FireSolidIcon v-if="actionsOpen" class="h-5 w-5 text-blue-600 dark:text-blue-400" />
      <FireIcon v-else class="h-5 w-5 text-fg-secondary dark:text-fg-muted-dark" />
    </button>

    <Transition
      enter-active-class="transition ease-out duration-100 origin-top-right"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75 origin-top-right"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="actionsOpen"
        data-tour="actions-menu-dropdown"
        class="absolute right-0 mt-2 w-56 rounded-lg bg-dropdown dark:bg-dropdown-dark shadow-lg border border-dropdown-border dark:border-dropdown-border-dark z-[100]"
        @click.stop
      >
        <div class="py-1">
          <slot name="action-items" :close="closeActionsDropdown" />
        </div>
      </div>
    </Transition>
  </div>

  <!-- User menu -->
  <div class="relative" ref="userMenuRef" data-tour="user-menu-wrapper">
    <button
      @click.stop="dropdownOpen = !dropdownOpen"
      data-tour="user-menu-trigger"
      class="p-2 rounded-lg bg-icon-btn dark:bg-icon-btn-dark transition-colors touch-target flex items-center justify-center"
      title="User menu"
      :aria-expanded="dropdownOpen"
      aria-haspopup="true"
    >
      <UserCircleSolidIcon v-if="dropdownOpen" class="h-5 w-5 text-blue-600 dark:text-blue-400" />
      <UserCircleIcon v-else class="h-5 w-5 text-fg-secondary dark:text-fg-muted-dark" />
    </button>

    <Transition
      enter-active-class="transition ease-out duration-100 origin-top-right"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75 origin-top-right"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="dropdownOpen"
        data-tour="user-menu-dropdown"
        class="absolute right-0 mt-2 w-56 rounded-lg bg-dropdown dark:bg-dropdown-dark shadow-lg border border-dropdown-border dark:border-dropdown-border-dark z-[100]"
        @click.stop
      >
        <div class="px-4 py-3 border-b border-dropdown-divider dark:border-dropdown-divider-dark">
          <p class="text-sm font-semibold text-fg-base dark:text-fg-base-dark truncate">{{ currentUser?.name }}</p>
          <p class="text-xs text-fg-muted dark:text-fg-muted-dark truncate mt-0.5">{{ currentUser?.email }}</p>
        </div>
        <div class="py-1">
          <slot name="menu-items" :close="closeDropdown" />
          <button
            @click="closeDropdown(); $emit('logout')"
            class="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 transition-colors"
          >
            <ArrowRightOnRectangleIcon class="h-4 w-4 mr-3 flex-shrink-0" />
            Logout
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { MoonIcon as MoonOutlineIcon, UserCircleIcon, ArrowRightOnRectangleIcon, FireIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { MoonIcon as MoonSolidIcon, UserCircleIcon as UserCircleSolidIcon, FireIcon as FireSolidIcon } from '@heroicons/vue/24/solid'
import { useGlobalSearch } from '../../composables/useGlobalSearch'

const globalSearch = useGlobalSearch()

defineProps({
  currentUser: { type: Object, default: null },
  isDarkMode: { type: Boolean, default: false },
})
defineEmits(['toggle-theme', 'logout'])

const dropdownOpen = ref(false)
const userMenuRef = ref(null)
const closeDropdown = () => { dropdownOpen.value = false }
// Sets state directly (idempotent — safe to fire more than once), rather than the tour
// simulating a click on a toggle button, which has no reliable way to know current state.
const forceOpenDropdown = () => { dropdownOpen.value = true }

const actionsOpen = ref(false)
const actionsMenuRef = ref(null)
const closeActionsDropdown = () => { actionsOpen.value = false }
// Same reasoning as forceOpenDropdown above — the tour's actions-menu step needs the
// dropdown genuinely expanded (not just the collapsed trigger) to spotlight it.
const forceOpenActionsDropdown = () => { actionsOpen.value = true }

const handleOutsideMouseDown = (e) => {
  if (userMenuRef.value && !userMenuRef.value.contains(e.target)) closeDropdown()
  if (actionsMenuRef.value && !actionsMenuRef.value.contains(e.target)) closeActionsDropdown()
}
const handleEscape = (e) => {
  if (e.key !== 'Escape' || !(dropdownOpen.value || actionsOpen.value)) return
  closeDropdown()
  closeActionsDropdown()
}
onMounted(() => {
  document.addEventListener('mousedown', handleOutsideMouseDown)
  document.addEventListener('keydown', handleEscape)
  window.addEventListener('tour:open-user-menu', forceOpenDropdown)
  window.addEventListener('tour:close-user-menu', closeDropdown)
  window.addEventListener('tour:open-actions-menu', forceOpenActionsDropdown)
})
onUnmounted(() => {
  document.removeEventListener('mousedown', handleOutsideMouseDown)
  document.removeEventListener('keydown', handleEscape)
  window.removeEventListener('tour:open-user-menu', forceOpenDropdown)
  window.removeEventListener('tour:close-user-menu', closeDropdown)
  window.removeEventListener('tour:open-actions-menu', forceOpenActionsDropdown)
})
</script>
